import {
  QueryKey,
  UseInfiniteQueryOptions,
  UseQueryOptions,
  useQuery,
} from 'react-query';

import {
  OutlookPage,
  getOutlookComponent,
  getOutlookComponentRenderItem,
  getPaginationOutlookPage,
} from '@api/chatbot';
import {
  IOutlookComponentSystem,
  IRenderedComponent,
} from '@base/outlookModel';
import { QUERY_KEYS } from '@constants';
import { IChatPagination } from '@model';
import { useChatPaginationQuery } from '@utils/query';

export const useOutlook = () => {
  const usePageOutlook = (
    pageName: OutlookPage,
    options?: Partial<
      UseInfiniteQueryOptions<
        IChatPagination<IRenderedComponent>,
        unknown,
        IChatPagination<IRenderedComponent>,
        IChatPagination<IRenderedComponent>,
        QueryKey
      >
    >,
    perPage = 20
  ) => {
    const query = useChatPaginationQuery(
      QUERY_KEYS.OUT_LOOK.PAGE(pageName),
      async ({ pageParam }) => {
        return await getPaginationOutlookPage(pageName, pageParam, perPage);
      },
      options
    );
    return query;
  };

  const useComponentOutlook = (
    key: string,
    options?: Omit<
      UseQueryOptions<
        IOutlookComponentSystem,
        unknown,
        IOutlookComponentSystem,
        string[]
      >,
      'queryKey' | 'queryFn'
    >
  ) => {
    const query = useQuery(
      QUERY_KEYS.OUT_LOOK.COMPONENT(key),
      () => getOutlookComponent(key),
      options
    );
    return query;
  };

  const useComponentOutlookRenderItem = <T>(
    key: string,
    options: Partial<UseInfiniteQueryOptions<IChatPagination<T>>>
  ) => {
    try {
      const query = useChatPaginationQuery(
        QUERY_KEYS.OUT_LOOK.COMPONENT_ITEM(key),
        ({ pageParam }) => getOutlookComponentRenderItem<T>(key, pageParam),
        {
          ...options,
        }
      );

      return query;
    } catch {
      return undefined;
    }
  };

  return {
    usePageOutlook,
    useComponentOutlook,
    useComponentOutlookRenderItem,
  };
};
